import axios from "axios";
import { baseUrl } from "./strapi-base-url.constant.ts";

// Site language (i18n) -> Strapi locale. `en` goes first: it is the CMS default locale with all original content.
const CMS_LOCALES: Record<string, string> = { eng: "en", ua: "uk" };
const ALL_LOCALES = Object.values(CMS_LOCALES);

// Site language first, then the other locales as a fallback for untranslated content
export const getCmsLocales = (language: string): string[] => {
  const primary = CMS_LOCALES[language] ?? "en";
  return [primary, ...ALL_LOCALES.filter((locale) => locale !== primary)];
};

const withLocale = (path: string, locale: string) =>
  `${baseUrl}/api/${path}${path.includes("?") ? "&" : "?"}locale=${locale}`;

// One entry per document: in the site language when translated, otherwise in another locale.
// Order follows the default locale list, documents that exist only in other locales go last.
export const fetchLocalizedCollection = async (path: string, language: string) => {
  const responses = await Promise.all(
    ALL_LOCALES.map((locale) => axios.get(withLocale(path, locale))),
  );
  const byLocale = new Map(ALL_LOCALES.map((locale, i) => [locale, responses[i].data.data]));

  const documentIds = [...new Set(responses.flatMap((res) => res.data.data.map((item) => item.documentId)))];

  return documentIds.map((documentId) => {
    for (const locale of getCmsLocales(language)) {
      const item = byLocale.get(locale).find((entry) => entry.documentId === documentId);
      if (item) return item;
    }
  });
};

// First non-empty result of `path` across locales, in the site language when possible
export const fetchLocalizedEntry = async (path: string, language: string) => {
  for (const locale of getCmsLocales(language)) {
    try {
      const { data } = await axios.get(withLocale(path, locale));
      const item = Array.isArray(data.data) ? data.data[0] : data.data;
      if (item) return item;
    } catch (err) {
      if (err.response?.status !== 404) throw err;
    }
  }
  return null;
};
